<script setup>
import { computed, ref, watch } from 'vue'
import { api } from '../../services/api'

// subject = null nghĩa là đang tạo môn mới.
const props = defineProps({ subject: { type: Object, default: null }, defaultGrade: { type: Number, default: 6 } })
const emit = defineEmits(['changed', 'select'])
const blank = () => ({ title: '', slug: '', grade: props.defaultGrade, description: '', icon: 'Aa', color: 'purple', is_published: false })
const form = ref(blank())
const semesterTitle = ref('')
const saving = ref(false)
const error = ref('')
const isNew = computed(() => !props.subject)
const nextSemester = computed(() => Math.max(0, ...(props.subject?.semesters || []).map(sem => sem.number)) + 1)
const suggestedSlug = computed(() => {
  const name = form.value.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const suffix = '-' + form.value.grade
  return name ? (name.endsWith(suffix) ? name.slice(0, 50) : name.slice(0, 50 - suffix.length).replace(/-$/, '') + suffix) : ''
})

watch(() => [props.subject, props.defaultGrade], ([subject]) => {
  form.value = subject ? { title: subject.title, slug: subject.slug, grade: subject.grade, description: subject.description || '', icon: subject.icon, color: subject.color, is_published: subject.is_published } : blank()
  error.value = ''
  semesterTitle.value = ''
}, { immediate: true })

async function run(task) {
  if (saving.value) return
  saving.value = true
  error.value = ''
  try { await task() }
  catch (err) { error.value = err.message }
  finally { saving.value = false }
}

const saveSubject = () => run(async () => {
  if (isNew.value) {
    const result = await api('/admin/subjects', { method: 'POST', body: JSON.stringify({ ...form.value, slug: form.value.slug.trim() || suggestedSlug.value, enroll_students: form.value.is_published }) })
    emit('changed', { select: `subject-${result.subject.id}`, message: `Đã tạo môn ${result.subject.title}. Tiếp theo, thêm học kỳ.` })
  } else {
    const { slug, ...payload } = form.value
    await api(`/admin/subjects/${props.subject.id}`, { method: 'PUT', body: JSON.stringify({ ...payload, enroll_students: payload.is_published }) })
    emit('changed', { message: 'Đã lưu thông tin môn học' })
  }
})

const addSemester = () => run(async () => {
  const result = await api('/admin/semesters', { method: 'POST', body: JSON.stringify({ subject_id: props.subject.id, number: nextSemester.value, title: semesterTitle.value.trim() }) })
  emit('changed', { select: `semester-${result.id}`, message: `Đã thêm ${result.title}. Tiếp theo, thêm chương / chủ đề.` })
})
</script>

<template>
  <div class="panel-body">
    <header class="panel-head"><div><p class="eyebrow">{{ isNew ? `Bước 1 · Tạo môn cho lớp ${form.grade}` : `Môn học · Lớp ${subject.grade}` }}</p><h2>{{ isNew ? 'Môn học mới' : subject.title }}</h2></div><span v-if="!isNew" class="chip" :class="subject.is_published ? '' : 'purple'">{{ subject.is_published ? 'Đang mở cho học sinh' : 'Nháp' }}</span></header>
    <p v-if="isNew" class="panel-hint">Tạo môn → thêm học kỳ → thêm chương → thêm bài. Mỗi bước xong, khung bên phải tự chuyển sang bước tiếp theo.</p>
    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>

    <section v-if="!isNew" class="panel-section">
      <div class="panel-section-head"><h3>Học kỳ</h3><span>{{ subject.semesters.length }}</span></div>
      <button v-for="sem in subject.semesters" :key="sem.id" type="button" class="panel-item" @click="emit('select', `semester-${sem.id}`)"><span><strong>{{ sem.title }}</strong><small>{{ sem.chapters.length }} chương · {{ sem.chapters.reduce((sum, chapter) => sum + chapter.lessons.length, 0) }} bài</small></span><b>→</b></button>
      <form class="inline-add" @submit.prevent="addSemester"><input v-model="semesterTitle" class="form-control" maxlength="100" :placeholder="`Học kỳ ${nextSemester}`" :disabled="saving" aria-label="Tên học kỳ mới"><button class="btn btn-primary" :disabled="saving">＋ Thêm học kỳ</button></form>
    </section>

    <form class="panel-section panel-form" @submit.prevent="saveSubject">
      <div v-if="!isNew" class="panel-section-head"><h3>Thông tin môn</h3></div>
      <div class="form-grid">
        <div class="form-group wide"><label for="subject-title">Tên môn học</label><input id="subject-title" v-model="form.title" class="form-control" required maxlength="100" :disabled="saving" placeholder="Ví dụ: Khoa học tự nhiên 6"></div>
        <div class="form-group"><label for="subject-grade">Lớp</label><input id="subject-grade" v-model.number="form.grade" class="form-control" type="number" min="1" max="12" required :disabled="saving"></div>
        <div class="form-group"><label for="subject-slug">Mã môn</label><input id="subject-slug" v-model="form.slug" class="form-control" maxlength="50" pattern="[a-z0-9]+(-[a-z0-9]+)*" :placeholder="suggestedSlug || 'Tự tạo từ tên môn'" :disabled="saving || !isNew"><small>{{ isNew ? 'Bỏ trống để dùng mã tự tạo.' : 'Mã môn được giữ cố định để nhập / xuất đúng môn.' }}</small></div>
        <div class="form-group"><label for="subject-icon">Biểu tượng</label><input id="subject-icon" v-model="form.icon" class="form-control" maxlength="10" required :disabled="saving"></div>
        <div class="form-group"><label for="subject-color">Màu</label><select id="subject-color" v-model="form.color" class="form-control" :disabled="saving"><option value="purple">Tím</option><option value="blue">Xanh</option><option value="orange">Cam</option></select></div>
        <div class="form-group wide"><label for="subject-description">Mô tả</label><textarea id="subject-description" v-model="form.description" class="form-control" :disabled="saving"></textarea></div>
        <label class="check-line wide"><input v-model="form.is_published" type="checkbox" :disabled="saving"><span>Xuất bản và đăng ký cho học sinh cùng lớp<small>Bỏ chọn để soạn ở trạng thái nháp.</small></span></label>
      </div>
      <button class="btn btn-primary" :disabled="saving">{{ saving ? 'Đang lưu…' : isNew ? 'Tạo môn học' : 'Lưu thông tin môn' }}</button>
    </form>
  </div>
</template>
